import { Inject, Injectable, InjectionToken, Optional } from '@angular/core'
import { type FieldValue, serverTimestamp, Timestamp } from 'firebase/firestore'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

export interface Timestamps {
  createdAt: Timestamp | null
  updatedAt: Timestamp | null
}

export type WithCreate<T> = T & { createdAt: FieldValue; updatedAt: FieldValue }
export type WithUpdate<T> = T & { updatedAt: FieldValue }

export type TimestampLike = Timestamp | Date | null | undefined

export interface TimestampFormatOptions extends Intl.DateTimeFormatOptions {
  pendingLabel?: string
}

export interface UseTimestampsDefaults {
  locale?: string | string[]
  timeZone?: string
  pendingLabel?: string
}

// Token para injetar as configurações padrão globalmente
export const TIMESTAMPS_DEFAULTS = new InjectionToken<UseTimestampsDefaults>('TIMESTAMPS_DEFAULTS')

@Injectable({
  providedIn: 'root'
})
export class TimestampsService {
  private locale?: string | string[]
  private timeZone?: string
  private pendingLabel: string

  constructor(@Optional() @Inject(TIMESTAMPS_DEFAULTS) defaults?: UseTimestampsDefaults) {
    this.locale = defaults?.locale
    this.timeZone = defaults?.timeZone
    this.pendingLabel = defaults?.pendingLabel ?? '—'
  }

  /** Sentinels for a brand-new document: sets createdAt + updatedAt. */
  stampNew<T extends object>(data: T): WithCreate<T> {
    return { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }
  }

  /** Sentinel for an edit: bumps updatedAt only, leaves createdAt untouched. */
  stampUpdate<T extends object>(data: T): WithUpdate<T> {
    return { ...data, updatedAt: serverTimestamp() }
  }

  getNewTimestamp() {
    return serverTimestamp()
  }

  /** JS Date -> Firestore Timestamp. Survives JSON serialization, unlike Date. */
  fromDate(date: Date): Timestamp {
    return Timestamp.fromDate(date)
  }

  /** Firestore Timestamp (or Date) -> JS Date, or null if pending/absent. */
  toDate(value: TimestampLike | string | number): Date | null {
    if (!value) return null
    if (value instanceof Date) return value
    if (typeof value === 'object' && 'toDate' in value) return value.toDate()
    if (typeof value === 'string') { const d = new Date(value); return isNaN(d.getTime()) ? null : d }
    if (typeof value === 'number') return new Date(value)
    return null
  }

  /** Absolute instant -> localized wall-clock string. */
  format(value: TimestampLike, options: TimestampFormatOptions = {}): string {
    const date = this.toDate(value)
    const { pendingLabel: localPending, ...intlOptions } = options

    if (!date) return localPending ?? this.pendingLabel

    return new Intl.DateTimeFormat(this.locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: this.timeZone,
      ...intlOptions,
    }).format(date)
  }

  /** "2 hours ago" style. Based on the absolute diff, so DST-agnostic. */
  formatRelative(value: TimestampLike, now: Date = new Date()): string {
    const date = this.toDate(value)
    if (!date) return this.pendingLabel

    const rtf = new Intl.RelativeTimeFormat(this.locale, { numeric: 'auto' })
    const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
      [60, 'second'],
      [60, 'minute'],
      [24, 'hour'],
      [7, 'day'],
      [4.34524, 'week'],
      [12, 'month'],
      [Number.POSITIVE_INFINITY, 'year'],
    ]

    let duration = (date.getTime() - now.getTime()) / 1000
    for (const [amount, unit] of divisions) {
      if (Math.abs(duration) < amount) return rtf.format(Math.round(duration), unit)
      duration /= amount
    }
    return rtf.format(Math.round(duration), 'year')
  }

  /**
   * Reactive wrapper (RxJS): pass an Observable, get a live formatted string Observable.
   * Equivalente ao `useFormattedTimestamp` do Vue.
   */
  formatObservable(
    source$: Observable<TimestampLike>,
    options: TimestampFormatOptions = {}
  ): Observable<string> {
    return source$.pipe(
      map(value => this.format(value, options))
    )
  }
}